import React, { useState } from "react";
import ReactDropzone from "react-dropzone";
import _ from "lodash";
import {
  Paper,
  Typography,
  CircularProgress,
  LinearProgress
} from "@material-ui/core";
import MuiIconButton from "@material-ui/core/IconButton";

import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import classNames from "../../utils/classNames";
import { Icon } from "../icons/Icon";

const FileDelete = (props: any) => {
  const [loading, setLoading] = useState(false);
  const { onClick, className } = props;
  if (loading) {
    return (
      <div className={className}>
        <CircularProgress size={18} />
      </div>
    );
  }

  return (
    <MuiIconButton
      className={className}
      onClick={() => {
        onClick();
        setLoading(true);
      }}
    >
      <Icon name="delete" />
    </MuiIconButton>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    dropzoneWrapper: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      marginRight: 20
    },
    dropButton: {
      minWidth: "200px",
      width: "100%",
      maxWidth: 300,
      border: `2px dashed ${theme.palette.primary.main}`,
      backgroundColor: "#fafafe",
      boxShadow: "unset"
    },
    dropzone: {
      height: 100,
      alignItems: "center",
      justifyContent: "center",
      display: "flex",
      flexGrow: 1,
      flexDirection: "column",
      padding: theme.spacing(2),
      cursor: "pointer"
    },
    loading: {
      width: 100,
      marginTop: theme.spacing(1)
    },
    fileList: {
      listStyle: "none",
      margin: 0,
      minWidth: "200px",
      padding: 0
    },
    fileListItem: {
      display: "flex",
      marginBottom: theme.spacing(1)
    },
    fileListText: {
      wordBreak: "break-all",
      width: 260,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      marginRight: theme.spacing(1)
    },
    fileListActionIcon: {
      marginLeft: theme.spacing(1),
      padding: 0,
      color: theme.palette.error.dark,
      alignItems: "center",
      display: "flex"
    },
    fileListActionWrapper: {
      display: "flex",
      height: "fit-content",
      alignItems: "center",
      marginLeft: theme.spacing(1)
    },
    fileListWrapper: {
      marginTop: theme.spacing(1)
    },
    hint: {
      textAlign: "left"
    },
    icon: {
      color: theme.palette.primary.main,
      display: "block",
      margin: "0 auto"
    },
    loadingWrapper: {
      justifyContent: "center",
      display: "flex"
    },
    text: {
      textAlign: "center"
    },
    root: {
      display: "flex",
      flexFlow: "row wrap",
      flexDirection: "column"
    }
  });

type Props = {
  acceptedFileTypes: string[];
  className?: string;
  classes: any;
  files: File[];
  hint?: string;
  id: string;
  loading?: boolean;
  maxFileCount?: number;
  maxTotalByteSize?: number;
  allowMultipleFiles?: boolean;
  onNewFiles?: (files: File[]) => void;
  onRemoveFile?: (file: File, index: number) => void;
  showFileList?: boolean;
};

type State = {
  error: Error | null;
};

class Dropzone extends React.Component<Props, State> {
  formatBytes: (bytes: number, decimals: number) => string;
  constructor(props: Props) {
    super(props);
    this.state = {
      error: null
    };

    this.formatBytes = (bytes: number, decimals: number = 2) => {
      // via https://stackoverflow.com/a/18650828/4757903
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + "" + sizes[i];
    };

    this.onDrop = this.onDrop.bind(this);
    this.onRemoveFile = this.onRemoveFile.bind(this);
  }

  getUniqueFiles = (existingFiles: File[], newFiles: File[]) => {
    return existingFiles.length > 0
      ? newFiles.filter(
          file =>
            !_.find(
              existingFiles,
              existingFile => existingFile.name === file.name
            )
        )
      : newFiles;
  };

  getTotalByteSize = (files: File[]) => {
    let bytes = 0;
    files.map(file => (bytes += file.size));
    return bytes;
  };

  onDrop = (files: File[]) => {
    const {
      files: existingFiles,
      maxFileCount,
      maxTotalByteSize,
      onNewFiles
    } = this.props;
    const uniqueNewFiles = this.getUniqueFiles(existingFiles, files);

    if (uniqueNewFiles.length === 0)
      return this.setState({
        error: new Error(`* File already attached`)
      });

    if (
      maxTotalByteSize &&
      this.getTotalByteSize(uniqueNewFiles) > maxTotalByteSize
    ) {
      return this.setState({
        error: new Error(
          `* The file is too large. Please select a file smaller than ${this.formatBytes(
            maxTotalByteSize,
            0
          )} and try again`
        )
      });
    }

    if (
      maxFileCount &&
      uniqueNewFiles.length + existingFiles.length > maxFileCount
    ) {
      return this.setState({
        error: new Error(
          maxFileCount > 1
            ? `* You may only upload up to ${maxFileCount} files`
            : "* You may only upload 1 file"
        )
      });
    }
    this.setState({
      error: null
    });
    onNewFiles && onNewFiles(uniqueNewFiles);
  };

  onRemoveFile(index: number) {
    const { files, onRemoveFile } = this.props;
    const file = files[index];
    onRemoveFile && onRemoveFile(file, index);
  }

  renderFileList() {
    const { classes, files } = this.props;
    return (
      <aside className={classes.fileListWrapper}>
        {files.length > 0 && (
          <ul className={classes.fileList}>
            {files.map((file: File, index: number) => (
              <li className={classes.fileListItem} key={file.name}>
                <Typography className={classes.fileListText}>
                  {file.name}
                </Typography>
                <div className={classes.fileListActionWrapper}>
                  <Typography color="textSecondary">
                    {this.formatBytes(file.size, 0)}
                  </Typography>

                  <FileDelete
                    className={classes.fileListActionIcon}
                    onClick={() => this.onRemoveFile(index)}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>
    );
  }

  render() {
    const {
      acceptedFileTypes,
      allowMultipleFiles = true,
      className,
      classes,
      hint,
      id,
      loading,
      showFileList = true
    } = this.props;
    const error = this.state.error;
    return (
      <div
        id={id}
        className={classNames(classes.root, className)}
        data-test-control={id}
      >
        <div className={classes.dropzoneWrapper}>
          {
            <ReactDropzone
              accept={acceptedFileTypes}
              onDrop={this.onDrop}
              multiple={allowMultipleFiles}
            >
              {({ getRootProps, getInputProps }) => (
                <Paper className={classes.dropButton}>
                  {loading ? (
                    <div className={classes.dropzone}>
                      <div>Uploading File...</div>
                      <LinearProgress className={classes.loading} />
                    </div>
                  ) : (
                    <div
                      {...getRootProps({
                        className: classes.dropzone
                      })}
                    >
                      <input {...getInputProps()} />
                      <Icon
                        className={classes.icon}
                        name="cloud_upload"
                        fontSize="large"
                      />
                      <Typography
                        color="textSecondary"
                        className={classes.text}
                      >
                        {`Drop ${
                          this.props.maxFileCount && this.props.maxFileCount > 1
                            ? "files"
                            : "file"
                        } here or click to browse`}
                      </Typography>
                    </div>
                  )}
                </Paper>
              )}
            </ReactDropzone>
          }
          {hint && (
            <Typography
              variant="caption"
              className={classes.hint}
              color="textSecondary"
            >
              {hint}
            </Typography>
          )}
          {error && (
            <Typography className={classes.hint} color="error">
              {error.message}
            </Typography>
          )}
        </div>
        {showFileList && this.renderFileList()}
      </div>
    );
  }
}

const styled = withStyles(styles)(Dropzone);
export { styled as Dropzone };
